/**
 * 认证上下文 — 支持五功能模块角色体系
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '@/services/api';
import type { User, TokenResponse } from '@/types/api';

interface FunctionInfo {
  role_id: number;
  role_name: string;
  function: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (account: string, password: string) => Promise<TokenResponse>;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
  // 功能模块
  functions: FunctionInfo[];
  currentFunction: string;
  currentFunctionRole: string;
  needSelect: boolean;
  switchFunction: (func: string, roleId?: number) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [functions, setFunctions] = useState<FunctionInfo[]>([]);
  const [currentFunction, setCurrentFunction] = useState('');
  const [currentFunctionRole, setCurrentFunctionRole] = useState('');
  const [needSelect, setNeedSelect] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('access_token');

    const initAuth = async () => {
      if (storedUser && token) {
        try {
          const userData = JSON.parse(storedUser);
          setUser(userData);
          setFunctions(JSON.parse(localStorage.getItem('functions') || '[]'));
          setCurrentFunction(localStorage.getItem('currentFunction') || '');
          setCurrentFunctionRole(localStorage.getItem('currentFunctionRole') || '');
          setIsLoading(false);

          // 异步刷新权限
          if (userData.account) {
            try {
              const permsResponse = await api.getUserPermissions(userData.account);
              const updatedUser = { ...userData, permissions: permsResponse.data || [] };
              localStorage.setItem('user', JSON.stringify(updatedUser));
              setUser(updatedUser);
            } catch { /* use cached */ }
          }
        } catch {
          localStorage.removeItem('user');
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
        }
      }
      setIsLoading(false);
    };
    initAuth();
  }, []);

  const login = useCallback(async (account: string, password: string): Promise<TokenResponse> => {
    setIsLoading(true);
    try {
      const response = await api.login({ account, password });
      const tokenData = response.data!;

      // 存储 functions 信息
      const funcs = tokenData.functions || [];
      setFunctions(funcs);
      setNeedSelect(tokenData.need_select ?? true);
      localStorage.setItem('functions', JSON.stringify(funcs));
      localStorage.setItem('needSelect', String(tokenData.need_select ?? true));

      // 登录后设置当前功能（如果无需选择）
      if (!tokenData.need_select && funcs.length > 0) {
        const fn = funcs[0].function;
        setCurrentFunction(fn);
        setCurrentFunctionRole(funcs[0].role_name);
        localStorage.setItem('currentFunction', fn);
        localStorage.setItem('currentFunctionRole', funcs[0].role_name);
      }

      // 获取用户信息
      const userResponse = await api.getUserByAccount(account);
      if (userResponse.data) {
        const permsResponse = await api.getUserPermissions(account);
        const userWithPerms = {
          ...userResponse.data,
          permissions: permsResponse.data || [],
        };
        localStorage.setItem('user', JSON.stringify(userWithPerms));
        setUser(userWithPerms);
        await new Promise(resolve => setTimeout(resolve, 0));
      }
      return tokenData;
    } catch (error) {
      api.setToken(null);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const switchFunction = useCallback(async (func: string, roleId?: number) => {
    const res = await api.request<{ access_token: string; refresh_token: string; function: string; function_role: string; permissions: string[] }>(
      '/auth/select-function',
      { method: 'POST', body: JSON.stringify({ function: func, role_id: roleId }) }
    );
    if (res.data) {
      api.setToken(res.data.access_token);
      localStorage.setItem('refresh_token', res.data.refresh_token);
      setCurrentFunction(res.data.function);
      setCurrentFunctionRole(res.data.function_role);
      localStorage.setItem('currentFunction', res.data.function);
      localStorage.setItem('currentFunctionRole', res.data.function_role);
      // Update user permissions — store in both localStorage AND state
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const updated = { ...currentUser, permissions: res.data.permissions };
      localStorage.setItem('user', JSON.stringify(updated));
      setUser(updated as User);
    }
  }, []);

  const logout = useCallback(() => {
    api.logout();
    localStorage.removeItem('user');
    localStorage.removeItem('functions');
    localStorage.removeItem('currentFunction');
    localStorage.removeItem('currentFunctionRole');
    localStorage.removeItem('needSelect');
    setUser(null);
    setFunctions([]);
    setCurrentFunction('');
  }, []);

  const hasPermission = useCallback((permission: string): boolean => {
    const perms = user?.permissions || [];
    try {
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      if (stored.permissions?.length) perms.push(...stored.permissions);
    } catch {}
    // 去重
    const all = [...new Set(perms)];
    // 精确匹配
    if (all.includes(permission)) return true;
    // 兼容无前缀匹配：权限 "exam:知识管理" 匹配检查 "知识管理"
    if (all.some(p => p.endsWith(`:${permission}`))) return true;
    // 反向兼容：权限 "知识管理" 匹配检查 "exam:知识管理"
    if (permission.includes(':') && all.includes(permission.split(':')[1])) return true;
    return false;
  }, [user]);

  return (
    <AuthContext.Provider value={{
      user, isAuthenticated: !!user, isLoading, login, logout, hasPermission,
      functions, currentFunction, currentFunctionRole, needSelect, switchFunction,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}
